/**
 * bimDemoPeople.ts
 *
 * The bridge between the demo run's `demo:people` feed (hub D4, plan §3.4) and
 * the 3D avatar layer's location seam (plan §4.3 step 5, card D8b): the run
 * publishes WHERE each cast member is **as a room name**, the layer places a
 * body in **world metres**. This module is the one conversion, pure and total.
 *
 * Two rules, both learned from this feature's own failure modes:
 *
 *  - **A cast key is not a user id.** The script's cast is authored by the film
 *    (marko / ana / jaka / pi) while the hub's people are rows with an id and a
 *    display name. The match is therefore normalised text against BOTH the id and
 *    the name (case, spaces and diacritics-insensitive enough for these names),
 *    and a cast member nothing matches answers nothing — the person keeps their
 *    own spawn, which is the layer's documented fallback. Never guess the pairing
 *    from position in the list: a re-ordered roster would then move everybody.
 *
 *  - **Only a ROOM has a place in the model.** `area` (the garden, the pool) and
 *    `trip` are real states in the run but have no room rectangle to stand in,
 *    and without a rectangle any invented point would park a body inside a wall.
 *    Those answer `null` (the person keeps their spawn) instead of a wrong place;
 *    the room name lookup is the caller's (the layout knows its own rooms
 *    document), so this module stays free of the viewer.
 */
import type { BimAvatarLocationMap } from '@/utils/bimAvatarPlacement';

/** One person, as `demo:people` publishes them (`DemoPersonBroadcast` on the hub). */
export interface DemoPersonRow {
  castKey: string;
  room: string | null;
  area: string | null;
  trip: string | null;
  activity: string;
}

/** A place in the model's own frame (world metres). */
export interface DemoRoomPoint {
  x: number;
  y: number;
  z: number;
}

/** How the layout turns a room NAME into a point (its rooms document, exact or case-insensitive). */
export type DemoRoomLookup = (room: string) => DemoRoomPoint | null;

/** One roster entry, as the avatar layer knows it. */
export interface DemoRosterPerson {
  id: string;
  name: string;
  /**
   * The hub's derived handle (`users.username`, e.g. `marko`) — the field a
   * script's cast keys actually name. The people directory carries it even when
   * the display name is empty, which is exactly the demo family's case.
   */
  handle?: string;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** The `demo:people` frame as rows — one per published person, garbage skipped. */
export function readDemoPeopleRows(payload: unknown): DemoPersonRow[] {
  if (!Array.isArray(payload)) return [];
  const rows: DemoPersonRow[] = [];
  for (const raw of payload) {
    const row = asRecord(raw);
    const castKey = text(row.castKey);
    if (castKey === '') continue;
    rows.push({
      castKey,
      room: text(row.room) === '' ? null : text(row.room),
      area: text(row.area) === '' ? null : text(row.area),
      trip: text(row.trip) === '' ? null : text(row.trip),
      activity: text(row.activity) || 'none',
    });
  }
  return rows;
}

/** A comparison key: lower case, letters and digits only (`Marko Savic` → `markosavic`). */
export function demoCastKey(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * The person a cast key names, or `null`. Exact (normalised) match on the id or
 * the name first; then `marko.savic` style ids split on their separators, and the
 * first word of a display name (`Marko Savič` → `marko`) as the last resort —
 * which is what a cast of first names (the film's) needs against hub rows that
 * carry full names.
 */
export function demoPersonForCastKey(
  castKey: string,
  people: DemoRosterPerson[],
): DemoRosterPerson | null {
  const wanted = demoCastKey(castKey);
  if (wanted === '') return null;
  for (const person of people) {
    if (demoCastKey(person.id) === wanted) return person;
    if (person.handle && demoCastKey(person.handle) === wanted) return person;
    if (demoCastKey(person.name) === wanted) return person;
  }
  for (const person of people) {
    const words = person.name.split(/[\s._-]+/).map((word) => demoCastKey(word)).filter(Boolean);
    // A single first name matches a full name's first word; a full name only
    // matches when every part is there (never `marko` against `markoana`).
    if (wanted.length >= 3 && (words[0] === wanted || words.includes(wanted))) return person;
  }
  return null;
}

/**
 * The provider's answers: a place per PERSON id, for the people the run places in
 * a room the model knows. Everyone else is absent from the map (the layer then
 * keeps their spawn), and a frame that places nobody answers `{}` — the seam's
 * "no opinion", never a set of nulls.
 */
export function demoPeoplePlaces(
  rows: DemoPersonRow[],
  people: DemoRosterPerson[],
  lookup: DemoRoomLookup,
): BimAvatarLocationMap {
  const places: BimAvatarLocationMap = {};
  for (const row of rows) {
    if (row.room === null) continue;
    const person = demoPersonForCastKey(row.castKey, people);
    if (!person) continue;
    let point: DemoRoomPoint | null = null;
    try {
      point = lookup(row.room);
    } catch {
      // A lookup that throws is "no place for this room" — never a dead frame.
      point = null;
    }
    if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y) || !Number.isFinite(point.z)) continue;
    places[person.id] = { x: point.x, y: point.y, z: point.z, yawDeg: 0 };
  }
  return places;
}
